/**
 * Web Navbar Item Component
 *
 * Single responsibility: Render a complete navbar menu item for web sidebar
 * Follows Single Responsibility Principle
 */

import React from 'react';
import { NavbarIcon, NavbarLabel } from '../../shared';
import { Badge } from '@/design-system';
import {
  typographyColors,
  backgroundColors,
  strokeColors,
  spacing,
  borders,
} from '@/design-system/utils';

export interface WebNavbarItemProps {
  id: string;
  label: string;
  iconName: string;
  isActive?: boolean;
  onClick?: () => void;
  badge?: string;
}

export const WebNavbarItem: React.FC<WebNavbarItemProps> = ({
  id,
  label,
  iconName,
  isActive = false,
  onClick,
  badge,
}) => {
  return (
    <button
      type='button'
      data-navbar-item-id={id}
      onClick={onClick}
      className='relative flex flex-col items-center outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0A0322]/30'
      style={{ gap: spacing[1] }}
      aria-pressed={isActive}
      aria-label={label}
      title={label}
    >
      <div
        className='relative transition-colors'
        style={{
          display: 'flex',
          minWidth: '40px',
          minHeight: '40px',
          padding: spacing[2], // 8px
          justifyContent: 'center',
          alignItems: 'center',
          gap: spacing[2], // 8px
          borderRadius: borders.radius.full, // 9999px
          border: isActive
            ? `${borders.width.thin} solid ${strokeColors.base}` // 1px solid #D6D3D1
            : `${borders.width.thin} solid transparent`,
          backgroundColor: isActive
            ? backgroundColors.shade // #E7E5E4
            : backgroundColors.primary,
        }}
      >
        <span
          className='relative z-10 flex items-center justify-center'
          style={{
            color: isActive
              ? typographyColors.primary
              : typographyColors.tertiary,
          }}
        >
          <NavbarIcon iconName={iconName} size={20} alt={label} />
        </span>
      </div>

      <NavbarLabel className='transition-all' fontWeight={isActive ? 600 : 400}>
        {label}
      </NavbarLabel>

      {badge && (
        <Badge
          variant='success'
          size='sm'
          className='bg-[#16A34A] text-white border-0 px-1 py-0.5 uppercase text-[8px]'
        >
          {badge}
        </Badge>
      )}
    </button>
  );
};
